import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { AlertTriangle, Printer } from 'lucide-react';
import { fmtDate } from '@/lib/api';

/**
 * Field pengiriman yang selalu dibutuhkan pada dokumen printout.
 * Export supaya bisa dipakai bersama antara PrintBatchDialog & child.
 */
export const DELIVERY_FIELDS = ['driver_name', 'vehicle_plate', 'delivery_address', 'delivery_date'];

/**
 * Return list nama field yang masih kosong untuk sebuah batch (fallback ke tx).
 * Digunakan untuk pre-flight check sebelum cetak & untuk indikator badge kuning.
 */
export function missingDeliveryFields(batch, tx) {
  const missing = [];
  DELIVERY_FIELDS.forEach((f) => {
    const bv = (batch?.[f] || '').toString().trim();
    if (bv) return;
    // Alamat pengiriman boleh fallback ke customer_address kalau tx.delivery_address kosong
    if (f === 'delivery_address') {
      const fromTx = (tx?.delivery_address || tx?.customer_address || '').toString().trim();
      if (!fromTx) missing.push(f);
      return;
    }
    const txv = (tx?.[f] || '').toString().trim();
    if (!txv) missing.push(f);
  });
  return missing;
}

/**
 * Baris list batch di dalam PrintBatchDialog.
 *
 * Props:
 *   - batch: object batch
 *   - tx: transaksi induk (untuk fallback info)
 *   - docType: 'so' | 'delivery-order' | 'invoice'
 *   - printing: bool — sedang cetak batch ini?
 *   - onPrint(batchIndex)
 */
export default function BatchListRow({ batch: b, tx, docType, printing, onPrint }) {
  const bMissing = missingDeliveryFields(b, tx);
  const bComplete = bMissing.length === 0;

  // Pilih nomor dokumen yang ditampilkan sesuai tipe cetak
  const docNo = docType === 'so'
    ? (b.so_no || `${tx.so_no}-B${b.batch_index}`)
    : docType === 'delivery-order'
      ? b.do_no
      : (b.invoice_no || `${tx.invoice_no}-B${b.batch_index}`);

  const totalQty = (b.items || []).reduce((a, i) => a + Number(i.quantity || 0), 0);
  const driver = b.driver_name || tx.driver_name;
  const plate = b.vehicle_plate || tx.vehicle_plate;
  const address = b.delivery_address || tx.delivery_address || tx.customer_address;
  const date = b.delivery_date || tx.delivery_date;

  return (
    <div
      className="rounded-md border p-2 flex items-start justify-between gap-2"
      data-testid={`print-batch-row-${b.batch_index}`}
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <Badge className="bg-primary/10 text-primary hover:bg-primary/10">Batch #{b.batch_index}</Badge>
          <span className="font-mono text-xs font-medium">{docNo}</span>
          <span className="text-[10px] text-muted-foreground">{fmtDate(b.created_at)}</span>
          {!bComplete && (
            <Badge variant="outline" className="text-[9px] border-amber-400 text-amber-700 bg-amber-50">
              <AlertTriangle className="h-2.5 w-2.5 mr-0.5" />
              Info pengiriman belum lengkap
            </Badge>
          )}
        </div>
        <div className="text-[10px] text-muted-foreground mt-0.5 space-y-0.5">
          <div>
            {totalQty} btg
            {driver && ` · Sopir: ${driver}`}
            {plate && ` · Plat: ${plate}`}
          </div>
          {address && <div className="truncate">Alamat: {address}</div>}
          {date && <div>Tgl Kirim: {fmtDate(date)}</div>}
        </div>
      </div>
      <Button
        size="sm"
        onClick={() => onPrint(b.batch_index)}
        disabled={printing}
        data-testid={`print-batch-btn-${b.batch_index}`}
      >
        <Printer className="h-3.5 w-3.5 mr-1" />
        {printing ? 'Mencetak...' : 'Cetak'}
      </Button>
    </div>
  );
}
